import { useState } from 'react';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';
import type { AgentParameterDto } from '../../types/agent';

interface ParameterInputProps {
  parameter: AgentParameterDto;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

/**
 * Component for inputting a single parameter value based on its type.
 */
export const ParameterInput = ({ parameter, value, onChange, error }: ParameterInputProps) => {
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleChange = (newValue: any) => {
    onChange(newValue);
    setJsonError(null);
  };

  const handleJsonChange = (jsonString: string) => {
    try {
      if (jsonString.trim()) {
        JSON.parse(jsonString);
        setJsonError(null);
      }
      handleChange(jsonString);
    } catch (e) {
      setJsonError('Invalid JSON');
      handleChange(jsonString);
    }
  };

  const renderInput = () => {
    switch (parameter.type.toLowerCase()) {
      case 'string':
        return (
          <TextField
            fullWidth
            label={parameter.label}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            required={parameter.isRequired}
            type={parameter.isSecret ? 'password' : 'text'}
            error={!!error}
            helperText={error || parameter.description}
            multiline={parameter.key.includes('prompt') || parameter.key.includes('template')}
            rows={parameter.key.includes('prompt') || parameter.key.includes('template') ? 4 : 1}
          />
        );

      case 'number':
      case 'int':
      case 'integer':
        return (
          <TextField
            fullWidth
            label={parameter.label}
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(Number(e.target.value))}
            required={parameter.isRequired}
            error={!!error}
            helperText={error || parameter.description}
          />
        );

      case 'double':
      case 'float':
      case 'decimal':
        return (
          <TextField
            fullWidth
            label={parameter.label}
            type="number"
            inputProps={{ step: 'any' }}
            value={value || ''}
            onChange={(e) => handleChange(Number(e.target.value))}
            required={parameter.isRequired}
            error={!!error}
            helperText={error || parameter.description}
          />
        );

      case 'boolean':
      case 'bool':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) => handleChange(e.target.checked)}
              />
            }
            label={parameter.label}
          />
        );

      case 'json':
      case 'object':
        return (
          <TextField
            fullWidth
            label={parameter.label}
            value={value || ''}
            onChange={(e) => handleJsonChange(e.target.value)}
            required={parameter.isRequired}
            error={!!error || !!jsonError}
            helperText={error || jsonError || parameter.description || 'Enter valid JSON'}
            multiline
            rows={6}
          />
        );

      case 'array':
        return (
          <TextField
            fullWidth
            label={parameter.label}
            value={value || ''}
            onChange={(e) => handleJsonChange(e.target.value)}
            required={parameter.isRequired}
            error={!!error || !!jsonError}
            helperText={error || jsonError || parameter.description || 'Enter valid JSON array'}
            multiline
            rows={4}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            label={parameter.label}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            required={parameter.isRequired}
            error={!!error}
            helperText={error || parameter.description}
          />
        );
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {renderInput()}
      {parameter.defaultValue && !value && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Default: {parameter.defaultValue}
        </Typography>
      )}
    </Box>
  );
};
